import React, { useState } from "react";
import { Button, Space, Typography } from "antd";
import { QRCodeSVG } from "qrcode.react";

import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";
import { verifyTotpSetupOperation } from "@api/services/identity";
import { VerificationCodeInput } from "../MobileVerificationRequest";

const LABEL_CODE = "Enter verification code from your authenticator app";
const LABEL_VERIFY = "Verify and Enable";
const LABEL_SETUP_KEY = "Use this key to add it manually";
const LABEL_ERROR = "Invalid or expired verification code";

type Props = {
  secretCode: string;
  setupUri: string;
  getAccessToken: () => Promise<string>;
  onSuccess: () => Promise<void> | void;
}

type RequestError = {
  code?: string;
}

const VerifyTotpSetup = ({
  secretCode,
  setupUri,
  getAccessToken,
  onSuccess,
}: Props) => {
  const { authenticatedRequest: request, showErrorMessage } = useAppContext() as AppContext;
  const [ isLoading, setIsLoading ] = useState(false);
  const [ code, setCode ] = useState("");
  const canVerify = code.length === 6;

  const verifySetup = async () => {
    setIsLoading(true);

    try {
      const accessToken = await getAccessToken();
      await request(
        verifyTotpSetupOperation,
        {
          mutation: {
            accessToken,
            code,
          },
        },
        [ "UnprocessibleConditionError" ],
      );

      await onSuccess();
    } catch (error) {
      const requestError = error as RequestError;
      const isExpected = requestError.code === "UnprocessibleConditionError";
      const isUnexpected = !isExpected;

      if (isUnexpected) {
        console.error(error);
        return;
      }

      showErrorMessage(LABEL_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text>
        <Typography.Text strong>Step 1</Typography.Text><br/>
        Scan the QR code with your authenticator app, or tap it to open setup:
      </Typography.Text>

      {/* NOTE: otpauth:// links are intercepted by Safari Passwords and
                iOS/Android authenticator apps, enabling one-tap setup */}
      <a href={setupUri} aria-label="Open authenticator app setup link">
        <QRCodeSVG value={setupUri} size={240} />
      </a>

      <Typography.Text>
        {LABEL_SETUP_KEY}:<br/>
        <Typography.Text code copyable>{secretCode}</Typography.Text>
      </Typography.Text>

      <Typography.Text>
        <Typography.Text strong>Step 2</Typography.Text><br/>
        {LABEL_CODE}:{' '}
        <VerificationCodeInput onChange={setCode} />
      </Typography.Text>

      <Button
        size="large"
        type="primary"
        onClick={verifySetup}
        loading={isLoading}
        disabled={!canVerify}
      >
        {LABEL_VERIFY}
      </Button>
    </Space>
  );
};

export default VerifyTotpSetup;
